import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, Lock, ChevronDown } from "lucide-react";
import confetti from "canvas-confetti";
import { useCountdown } from "../hooks/useCountdown";
import { trackEvent } from "../tracking";
import SlotMachineModal from "../components/SlotMachineModal";

function CountdownDisplay() {
  const { days, hours, mins, secs } = useCountdown();
  return (
    <div className="flex items-center gap-3 text-amber-500">
      <span className="text-[10px] font-bold tracking-widest text-white/40 uppercase">Event in:</span>
      <div className="flex gap-2 font-mono text-sm md:text-base font-bold">
        <span>{String(days).padStart(2, "0")}D</span>
        <span className="opacity-30">:</span>
        <span>{String(hours).padStart(2, "0")}H</span>
        <span className="opacity-30">:</span>
        <span>{String(mins).padStart(2, "0")}M</span>
        <span className="opacity-30">:</span>
        <span>{String(secs).padStart(2, "0")}S</span>
      </div>
    </div>
  );
}

export default function CharlotteLanding({ source = "organic" }) {
  const navigate = useNavigate();
  const [showSpinner, setShowSpinner] = useState(true);

  useEffect(() => {
    trackEvent(`charlotte_${source}_LandingView`, {
      content_name: `charlotte_${source}_landing`,
      content_category: "charlotte_event",
      value: 50,
      currency: "USD",
    });
  }, [source]);

  function handleCTA() {
    trackEvent(`charlotte_${source}_CTAClick`, {
      content_name: `charlotte_${source}_cta`,
      content_category: "charlotte_event",
      value: 50,
      currency: "USD",
    });
    confetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 },
      colors: ["#f59e0b", "#C9ED00", "#ffffff"],
    });
    setTimeout(() => navigate("/charlotte-form"), 600);
  }

  function handleSpinnerClaim() {
    setShowSpinner(false);
    trackEvent(`charlotte_${source}_CTAClick`, {
      content_name: `charlotte_${source}_spinner_claim`,
      content_category: "charlotte_event",
      value: 50,
      currency: "USD",
    });
    navigate("/charlotte-form");
  }

  function handleSpinnerSkip() {
    setShowSpinner(false);
  }

  const WHATSAPP_URL = "https://wa.me/19193082230?text=Hi%2C%20I%20downloaded%20remit2any%20and%20completed%20KYC.%20Please%20send%20me%20my%20RAM%20Miriyala%20%2420%20bonus%20code%21%20%F0%9F%8E%9F%EF%B8%8F";

  function handleWhatsApp() {
    trackEvent(`charlotte_${source}_WhatsAppClaimClick`, {
      content_name: `charlotte_${source}_whatsapp_claim`,
      content_category: "charlotte_event",
    });
    window.open(WHATSAPP_URL, "_blank");
  }

  return (
    <div className="min-h-screen bg-[#050505] relative flex flex-col overflow-x-hidden">

      {showSpinner && (
        <SlotMachineModal
          onClaim={handleSpinnerClaim}
          onSkip={handleSpinnerSkip}
          onWhatsApp={handleWhatsApp}
          eventLabel="Your exclusive Charlotte fan deal"
        />
      )}

      {/* Top notification strip */}
      <div className="w-full bg-amber-500/10 border-b border-amber-500/20 py-3 text-center relative z-30">
        <p className="text-[10px] md:text-xs font-bold text-amber-400 tracking-[0.3em] uppercase px-4">
          YOU'VE UNLOCKED AN EXCLUSIVE FAN DEAL
        </p>
      </div>

      {/* Background layers */}
      <div className="absolute inset-0 z-0 pointer-events-none select-none">
        <img
          src="/heroimage.jpeg"
          alt=""
          className="w-full h-full object-cover object-top opacity-30 grayscale"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 via-[#050505]/40 to-[#050505]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/50 via-transparent to-[#050505]/50" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(201, 237, 0, 0.3) 0%, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
      </div>

      {/* Main content */}
      <main className="flex-1 flex flex-col items-center justify-center container mx-auto px-6 py-12 relative z-20">

        {/* Burst glow + badge */}
        <div className="relative mb-10">
          <div
            className="absolute top-1/2 left-1/2 w-48 h-48 bg-amber-500/20 rounded-full blur-3xl"
            style={{
              transform: "translate(-50%, -50%)",
              animation: "burst-glow 4s ease-in-out infinite",
            }}
          />
          <div className="relative inline-flex items-center gap-2 px-6 py-2.5 bg-amber-500 rounded-full text-[11px] font-black text-black tracking-wider uppercase shadow-[0_0_30px_rgba(245,158,11,0.4)]">
            🏆 SELECTED FOR EXCLUSIVE PRICING
          </div>
        </div>

        <div className="max-w-2xl w-full">
          {/* Headline */}
          <div className="text-center md:text-left mb-8 space-y-2">
            <h1 className="text-[14vw] md:text-[8rem] font-serif leading-[0.8] tracking-tight drop-shadow-2xl flex flex-col md:flex-row items-center md:items-end md:gap-4">
              <span className="text-[#C9ED00]">RAM</span>
              <span className="text-white">MIRIYALA</span>
            </h1>
            <p className="text-2xl md:text-3xl font-light text-white/50 tracking-wide mt-4 uppercase">
              Live in Concert
            </p>
          </div>

          {/* Event detail pills */}
          <div className="flex flex-wrap justify-center md:justify-start gap-3 mb-10">
            {[
              "📍 Charlotte, NC",
              "🎤 Ram Miriyala",
              "⏰ Doors at 9PM",
            ].map((item) => (
              <div
                key={item}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-white/80 backdrop-blur-sm"
              >
                {item}
              </div>
            ))}
          </div>

          {/* Offer card */}
          <div className="relative group mb-10">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-[#C9ED00]/20 rounded-[2rem] blur opacity-75" />
            <div className="relative bg-white/5 border border-white/10 backdrop-blur-xl rounded-[2rem] p-8 md:p-10 shadow-2xl overflow-hidden">
              <div
                className="absolute top-0 right-0 w-32 h-32 pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(201,237,0,0.1) 0%, transparent 70%)",
                  filter: "blur(40px)",
                }}
              />

              {/* Price row */}
              <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
                <div className="text-center md:text-left">
                  <span className="text-[10px] font-bold tracking-[3px] text-white/40 uppercase mb-2 block">
                    YOUR EXCLUSIVE PRICE
                  </span>
                  <div className="flex items-baseline gap-4">
                    <span className="text-6xl md:text-7xl font-serif text-white font-bold">$50</span>
                  </div>
                </div>

                {/* remit2any bonus badge */}
                <div className="flex flex-col items-center md:items-end gap-3">
                  <div className="px-4 py-2 bg-[#C9ED00]/10 border border-[#C9ED00]/30 rounded-xl text-center">
                    <p className="text-[10px] text-white/40 uppercase tracking-widest mb-0.5">Sponsor Bonus</p>
                    <p className="text-[#C9ED00] text-lg font-black">+ $20 FREE</p>
                    <p className="text-[10px] text-white/40">via remit2any app</p>
                  </div>
                  <CountdownDisplay />
                </div>
              </div>

              {/* Net value line */}
              <div className="border-t border-white/5 pt-4 flex items-center justify-between">
                <span className="text-[10px] text-white/30 uppercase tracking-widest">Effective cost after bonus</span>
                <span className="text-white font-black text-xl">$30</span>
              </div>

              {/* Prize draw upside line */}
              <div className="border-t border-[#C9ED00]/10 mt-4 pt-4 flex items-center justify-between gap-4">
                <span className="text-[10px] text-white/30 uppercase tracking-widest leading-relaxed">Show up. Maybe walk out with a MacBook.</span>
                <span className="text-[#C9ED00] font-black text-sm whitespace-nowrap">It's free.</span>
              </div>
            </div>
          </div>

          {/* Prize draw section */}
          <div className="relative mb-10">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-[#C9ED00]/20 to-amber-500/20 rounded-[2rem] blur opacity-60" />
            <div className="relative bg-white/5 border border-[#C9ED00]/20 backdrop-blur-xl rounded-[2rem] p-8 overflow-hidden">
              <div
                className="absolute top-0 right-0 w-40 h-40 pointer-events-none"
                style={{
                  background: "radial-gradient(circle, rgba(201,237,0,0.08) 0%, transparent 70%)",
                  filter: "blur(40px)",
                }}
              />

              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-[10px] font-bold tracking-[3px] text-[#C9ED00] uppercase mb-1">
                    Powered by remit2any
                  </p>
                  <h3 className="text-white font-black text-xl leading-tight">Free Prize Giveaway</h3>
                  <p className="text-white/40 text-xs mt-1">Come to the show. remit2any gives away prizes on stage. Your ticket gets you in automatically — no extra signup needed.</p>
                </div>
                <div className="px-3 py-1.5 bg-[#C9ED00]/10 border border-[#C9ED00]/30 rounded-full flex-shrink-0 ml-4">
                  <p className="text-[#C9ED00] text-[10px] font-black tracking-widest uppercase">Free</p>
                </div>
              </div>

              {/* Prize tiles */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                {[
                  { emoji: "🎧", name: "AirPods Pro", value: "$249" },
                  { emoji: "💻", name: "MacBook Neo", value: "$600+" },
                  { emoji: "🎁", name: "Gift Cards", value: "$200–$600" },
                ].map((prize) => (
                  <div
                    key={prize.name}
                    className="bg-white/5 border border-white/10 rounded-2xl p-4 text-center"
                    style={{ boxShadow: "inset 0 0 20px rgba(201,237,0,0.03)" }}
                  >
                    <div className="text-3xl mb-2">{prize.emoji}</div>
                    <p className="text-white text-[11px] font-bold leading-tight mb-1">{prize.name}</p>
                    <p className="text-[#C9ED00] text-xs font-black">{prize.value}</p>
                  </div>
                ))}
              </div>

              {/* Bottom summary */}
              <div className="bg-[#C9ED00]/5 border border-[#C9ED00]/20 rounded-xl px-5 py-4">
                <p className="text-white font-black text-sm mb-1">You paid $30. You show up. You can win one of these.</p>
                <p className="text-white/40 text-xs">Your ticket gets you in automatically — no extra signup needed.</p>
              </div>
            </div>
          </div>

          {/* Proof screenshot */}
          <div className="relative mb-10">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-500/20 to-[#C9ED00]/20 rounded-[2rem] blur opacity-60" />
            <div className="relative bg-white/5 border border-white/10 rounded-[2rem] p-5 flex flex-col sm:flex-row items-center gap-5 backdrop-blur-sm overflow-hidden">
              <img
                src="/remit2any-proof.png"
                alt="Real $20 credited to remit2any USD wallet"
                className="w-36 rounded-2xl shadow-xl flex-shrink-0"
              />
              <div>
                <p className="text-[10px] font-bold tracking-[3px] text-white/40 uppercase mb-2">
                  Real proof — not a mockup
                </p>
                <p className="text-white font-bold text-lg leading-tight mb-1">
                  $20 credited instantly
                </p>
                <p className="text-white/50 text-sm leading-relaxed">
                  Promotional Credit · USD Wallet · Apr 29, 2026
                </p>
                <p className="text-[11px] text-amber-400/70 mt-2 flex items-center gap-1">
                  🛡️ Backed by Cross River Bank (Member FDIC)
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col items-center space-y-3">
            <button
              onClick={handleCTA}
              className="w-full flex items-center justify-center px-10 py-6 bg-amber-500 text-black rounded-full font-black text-lg md:text-xl transition-all duration-300 hover:bg-amber-400 hover:scale-[1.02] shadow-[0_0_50px_rgba(245,158,11,0.3)]"
              style={{ animation: "cta-pulse 2s infinite" }}
            >
              CLAIM TICKET + $20 BUNDLE →
            </button>
            <button
              onClick={handleWhatsApp}
              className="w-full flex items-center justify-center gap-3 py-5 rounded-full font-black text-base transition-all duration-200 hover:scale-[1.02] shadow-[0_0_30px_rgba(37,211,102,0.25)]"
              style={{ background: "#25D366", color: "#000" }}
            >
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Already Done KYC? Claim via WhatsApp →
            </button>

            <div className="flex flex-col md:flex-row items-center gap-6 opacity-70">
              <div className="flex items-center gap-1.5">
                <div className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400" />
                  ))}
                </div>
                <span className="text-[11px] font-bold tracking-wide uppercase ml-1">
                  300+ members already visiting
                </span>
              </div>
              <div className="flex items-center gap-4 text-xs font-medium text-white/50">
                <span className="flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Secure Checkout
                </span>
                <span>•</span>
                <span>18+ Event</span>
                <span>•</span>
                <span>Free Parking</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Scroll indicator */}
      <div className="pb-28 md:pb-10 flex justify-center opacity-30">
        <ChevronDown className="w-6 h-6 text-white animate-bounce" />
      </div>

      {/* Sticky bottom CTA — always visible on mobile */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 md:hidden px-4 pb-5 pt-3"
        style={{ background: "linear-gradient(to top, #050505 60%, transparent)" }}
      >
        <div className="grid grid-cols-2 gap-2">
          <button
            onClick={handleCTA}
            className="flex items-center justify-center py-4 bg-amber-500 text-black rounded-full font-black text-sm shadow-[0_0_30px_rgba(245,158,11,0.5)]"
            style={{ animation: "cta-pulse 2s infinite" }}
          >
            Claim Bundle →
          </button>
          <button
            onClick={handleWhatsApp}
            className="flex items-center justify-center gap-1.5 py-4 rounded-full font-black text-sm"
            style={{ background: "#25D366", color: "#000" }}
          >
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Done KYC
          </button>
        </div>
        <p className="text-center text-[10px] text-white/30 mt-2 tracking-widest uppercase">
          300+ members already visiting
        </p>
      </div>

      <style>{`
        @keyframes burst-glow {
          0%, 100% { opacity: 0.2; transform: translate(-50%, -50%) scale(1); }
          50%       { opacity: 0.4; transform: translate(-50%, -50%) scale(1.2); }
        }
        @keyframes cta-pulse {
          0%   { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
          70%  { box-shadow: 0 0 0 15px rgba(245, 158, 11, 0); }
          100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
        }
      `}</style>
    </div>
  );
}
